'use client';

import { FC, useEffect, useRef } from 'react';
import { Content, isFilled } from '@prismicio/client';
import { PrismicNextImage, PrismicNextLink } from '@prismicio/next';
import { PrismicRichText, SliceComponentProps } from '@prismicio/react';

/**
 * Props for `HighlightedCardsGrid`.
 */
export type HighlightedCardsGridProps =
  SliceComponentProps<Content.HighlightedCardsGridSlice>;

/**
 * Component for "HighlightedCardsGrid" Slices.
 */
const HighlightedCardsGrid: FC<HighlightedCardsGridProps> = ({ slice }) => {
  const { title, description, cards } = slice.primary;
  const cardRefs = useRef<(HTMLAnchorElement | HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -10% 0px',
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-slide-in-from-bottom');
          entry.target.classList.remove('opacity-0', 'translate-y-12');
        }
      });
    }, observerOptions);

    const currentElements: (HTMLAnchorElement | HTMLDivElement | null)[] = [];

    cardRefs.current.forEach((card, index) => {
      if (card) {
        currentElements.push(card);
        card.classList.add(
          'opacity-0',
          'translate-y-12',
          'transition-all',
          'duration-700',
          'ease-out',
        );
        card.style.transitionDelay = `${index * 100}ms`;
        observer.observe(card);
      }
    });

    return () => {
      currentElements.forEach((element) => {
        if (element) {
          observer.unobserve(element);
        }
      });
    };
  }, [cards]);

  return (
    <section
      className="py-12 md:py-20"
      style={{
        background: 'linear-gradient(180deg, #0F423A 0%, #0E1010 100%)',
      }}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8 md:mb-12">
          <PrismicRichText
            field={title}
            components={{
              heading2: ({ children }) => (
                <h2 className="text-4xl md:text-5xl font-bold text-white">
                  {children}
                </h2>
              ),
            }}
          />
          <div className="max-w-3xl mt-4">
            <PrismicRichText
              field={description}
              components={{
                paragraph: ({ children }) => (
                  <p className="text-lg text-gray-300">{children}</p>
                ),
              }}
            />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {cards.map((card, index) => {
            const hasLink = isFilled.link(card.link_to_case_study);

            if (hasLink) {
              return (
                <PrismicNextLink
                  key={index}
                  ref={(el) => {
                    cardRefs.current[index] = el;
                  }}
                  field={card.link_to_case_study}
                  className="overflow-hidden shadow-lg group transform transition-transform duration-300 hover:scale-105 flex flex-col h-[450px] cursor-pointer"
                  style={{ borderRadius: '24px' }}
                >
                  {/* Top section for text */}
                  <div
                    className="p-6 flex-grow flex flex-col"
                    style={{
                      flexBasis: '60%',
                      background:
                        'radial-gradient(1786.22% 95.9% at 95.9% 0%, rgba(255, 255, 255, 0.08) 0%, rgba(3, 191, 158, 0.15) 46.67%, rgba(255, 255, 255, 0.15) 100%)',
                    }}
                  >
                    <span className="text-sm font-bold uppercase text-green-400">
                      {card.category}
                    </span>
                    <div className="my-auto">
                      <PrismicRichText
                        field={card.card_title}
                        components={{
                          heading4: ({ children }) => (
                            <h4 className="text-2xl font-bold text-white">
                              {children}
                            </h4>
                          ),
                        }}
                      />
                    </div>
                    <p className="text-sm font-bold uppercase text-green-400 mt-auto">
                      {card.card_subcategory}
                    </p>
                  </div>

                  {/* Bottom section for image and logo */}
                  <div
                    className="relative bg-gray-900"
                    style={{ flexBasis: '40%' }}
                  >
                    <PrismicNextImage
                      field={card.card_image}
                      className="absolute inset-0 w-full h-full object-cover"
                      alt=""
                    />
                    <div className="absolute inset-0 bg-black/20 flex items-center justify-center p-4">
                      <PrismicNextImage
                        field={card.logo}
                        className="w-auto h-auto max-w-full max-h-full object-contain"
                        alt=""
                      />
                    </div>
                  </div>
                </PrismicNextLink>
              );
            }

            return (
              <div
                key={index}
                ref={(el) => {
                  cardRefs.current[index] = el;
                }}
                className="overflow-hidden shadow-lg group transform transition-transform duration-300 hover:scale-105 flex flex-col h-[450px]"
                style={{ borderRadius: '24px' }}
              >
                {/* Top section for text */}
                <div
                  className="p-6 flex-grow flex flex-col"
                  style={{
                    flexBasis: '60%',
                    background:
                      'radial-gradient(1786.22% 95.9% at 95.9% 0%, rgba(255, 255, 255, 0.08) 0%, rgba(3, 191, 158, 0.15) 46.67%, rgba(255, 255, 255, 0.15) 100%)',
                  }}
                >
                  <span className="text-sm font-bold uppercase text-green-400">
                    {card.category}
                  </span>
                  <div className="my-auto">
                    <PrismicRichText
                      field={card.card_title}
                      components={{
                        heading4: ({ children }) => (
                          <h4 className="text-2xl font-bold text-white">
                            {children}
                          </h4>
                        ),
                      }}
                    />
                  </div>
                  <p className="text-sm font-bold uppercase text-green-400 mt-auto">
                    {card.card_subcategory}
                  </p>
                </div>

                {/* Bottom section for image and logo */}
                <div
                  className="relative bg-gray-900"
                  style={{ flexBasis: '40%' }}
                >
                  <PrismicNextImage
                    field={card.card_image}
                    className="absolute inset-0 w-full h-full object-cover"
                    alt=""
                  />
                  <div className="absolute inset-0 bg-black/20 flex items-center justify-center p-4">
                    <PrismicNextImage
                      field={card.logo}
                      className="w-auto h-auto max-w-full max-h-full object-contain"
                      alt=""
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default HighlightedCardsGrid;
